import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import Topbar from './Topbar';
import RadiologyResultsModal5 from './RadiologyResultsModal5';
import './RadiographerDashboard.css';

const RadiographerDashboard = () => {
  const [pendingExamsCount, setPendingExamsCount] = useState(0);
  const [recentExams, setRecentExams] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const [clinicDetails, setClinicDetails] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isRadiologyResultsModalOpen, setIsRadiologyResultsModalOpen] = useState(false);
  const [stats, setStats] = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    total: 0,
    pending: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = useRef(params.get('token'));

  const formatDateToDDMMYYYY = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const isSameDate = (patientDateString, searchDateString) => {
    if (!searchDateString) return true;
    
    const patientDate = new Date(patientDateString);
    const searchDateObj = new Date(searchDateString);
    
    searchDateObj.setDate(searchDateObj.getDate() + 0);
    
    return patientDate.getFullYear() === searchDateObj.getFullYear() &&
           patientDate.getMonth() === searchDateObj.getMonth() &&
           patientDate.getDate() === searchDateObj.getDate();
  };

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const tokenFromUrl = urlToken.current;

        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (!securityResponse.ok) throw new Error('Failed to perform security check');

        const securityData = await securityResponse.json();

        if (securityData.message === 'Session valid') {
          setEmployeeName(securityData.employee_name);
          await Promise.all([
            fetchRecentExams(tokenFromUrl), 
            fetchClinicDetails(tokenFromUrl),
            fetchRadiologyStats(tokenFromUrl)
          ]);
        } else if (securityData.error === 'Session expired') {
          navigate(`/dashboard?token=${securityData.clinic_session_token}`);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  useEffect(() => {
    const fetchPendingExamsCount = async (token) => {
      try {
        const response = await fetch(urls.pendingexamscount, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) throw new Error('Failed to fetch pending exams count');

        const data = await response.json();
        setPendingExamsCount(data.count);
        setStats(prev => ({...prev, pending: data.count}));
      } catch (error) {
        console.error('Error fetching pending exams count:', error);
      }
    };

    const tokenFromUrl = urlToken.current;
    if (tokenFromUrl) {
      fetchPendingExamsCount(tokenFromUrl);
      const interval = setInterval(() => fetchPendingExamsCount(tokenFromUrl), 7000);
      return () => clearInterval(interval);
    }
  }, []);

  const fetchRadiologyStats = async (token) => {
    try {
      const response = await fetch(urls.fetchRadiologyStat, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          today: data.today || 0,
          thisWeek: data.thisWeek || 0,
          thisMonth: data.thisMonth || 0,
          thisYear: data.thisYear || 0,
          total: data.total || 0,
          pending: stats.pending
        });
      } else {
        throw new Error('Failed to fetch radiology stats');
      }
    } catch (error) {
      console.error('Error fetching radiology stats:', error);
    }
  };

  const fetchRecentExams = async (token) => {
    try {
      const response = await fetch(urls.allradiology, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        setRecentExams(data.patients || []);
      } else {
        throw new Error('Failed to fetch recent radiology exams');
      }
    } catch (error) {
      console.error('Error fetching recent radiology exams:', error);
    }
  };

  const fetchClinicDetails = async (token) => {
    try {
      const response = await fetch(urls.fetchclinicdetails, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        setClinicDetails(data);
      } else {
        throw new Error('Failed to fetch clinic details');
      }
    } catch (error) {
      console.error('Error fetching clinic details:', error);
    }
  };

  const handleShowRadiologyResults = (patient) => {
    setSelectedPatient({
      ...patient,
      clinicDetails,
    });
    setIsRadiologyResultsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsRadiologyResultsModalOpen(false);
  };

  const handleGoToPendingExams = () => {
    const tokenFromUrl = urlToken.current;
    navigate(`/radiology?token=${tokenFromUrl}`);
  };

  const filteredExams = recentExams.filter(patient => {
    const matchesName = `${patient.first_name} ${patient.last_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    
    const matchesDate = isSameDate(patient.date_time, searchDate);
    
    return matchesName && (searchDate ? matchesDate : true);
  });

  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      paddingTop: '75px'
    }}>
      <Topbar token={urlToken.current} />

      {/* Pending Exams Alert */}
      <div style={{
        padding: '15px 0',
        backgroundColor: pendingExamsCount > 0 ? '#fff8f8' : '#f8f8f8',
        borderBottom: pendingExamsCount > 0 ? '1px solid #ffdddd' : '1px solid #e0e0e0',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: pendingExamsCount > 0 ? '#d32f2f' : '#616161',
        fontWeight: '500',
        cursor: pendingExamsCount > 0 ? 'pointer' : 'default',
        animation: pendingExamsCount > 0 ? 
          'urgent-blink 2s infinite, urgent-shake 3s infinite' : 'none'
      }} onClick={pendingExamsCount > 0 ? handleGoToPendingExams : null}>
        <div style={{
          maxWidth: '1200px',
          width: '100%',
          textAlign: 'center',
          padding: '0 20px'
        }}>
          {pendingExamsCount > 0 ? (
            `Hello ${employeeName}, you have ${pendingExamsCount} patient${pendingExamsCount > 1 ? 's' : ''} waiting for radiology services. Click here to see them.`
          ) : (
            `Hello ${employeeName}, there are no patients waiting for radiology services at the moment.`
          )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '0 20px',
        gap: '30px'
      }}>
        {/* Left Side - Stats */}
        <div style={{
          flex: '0 0 300px',
          height: 'calc(100vh - 180px)',
          overflowY: 'auto',
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
          position: 'sticky',
          top: '100px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#212121',
            marginBottom: '25px',
            paddingBottom: '10px',
            borderBottom: '1px solid #e0e0e0'
          }}>RADIOLOGY STATISTICS</h2>
          
          <div style={{ display: 'grid', gap: '15px' }}>
            <StatCard 
              title="Exams Done Today" 
              value={stats.today} 
              icon="📅"
            />
            <StatCard 
              title="Exams Done This Week" 
              value={stats.thisWeek} 
              icon="🗓️"
            />
            <StatCard 
              title="Exams Done This Month" 
              value={stats.thisMonth} 
              icon="📆"
            />
            <StatCard 
              title="Exams Done This Year" 
              value={stats.thisYear} 
              icon="📊"
            />
            <StatCard 
              title="Total Exams Done" 
              value={stats.total} 
              icon="🩺"
            />
            <StatCard 
              title="Pending Exams" 
              value={stats.pending} 
              icon="⏳"
            />
          </div>
        </div>

        {/* Right Side - Recent Exams */}
        <div style={{
          flex: '1',
          minWidth: 0, // Prevent flex item from overflowing
          height: 'calc(100vh - 180px)',
          overflowY: 'auto',
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '25px',
            paddingBottom: '15px',
            borderBottom: '1px solid #e0e0e0',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#212121',
              margin: 0
            }}>PATIENT EXAMINATIONS</h2>
            
            <div style={{ 
              display: 'flex', 
              gap: '15px',
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    paddingLeft: '35px',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0',
                    fontSize: '14px',
                    width: '180px'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9e9e9e'
                }}>🔍</span>
              </div>
              
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  placeholder="Search by date..."
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    paddingLeft: '35px',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0',
                    fontSize: '14px',
                    width: '180px'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9e9e9e'
                }}>📅</span>
              </div>
            </div>
          </div>

          {filteredExams.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 0',
              color: '#757575'
            }}>
              {recentExams.length === 0 ? (
                'No radiology exams available.'
              ) : (
                'No matching exams found. Try adjusting your search criteria.'
              )}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px',
              gridAutoRows: 'minmax(200px, auto)',
              alignItems: 'stretch'
            }}>
              {filteredExams.map((patient) => (
                <div
                  key={patient.file_id}
                  style={{
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #f5f5f5',
                    flexShrink: 0
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#212121',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '180px'
                    }}>
                      {patient.first_name} {patient.last_name}
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      color: '#757575',
                      backgroundColor: '#f5f5f5',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      flexShrink: 0
                    }}>
                      {patient.age} {patient.sex}
                    </span>
                  </div>

                  <div style={{ 
                    marginBottom: '15px',
                    flex: 1,
                    overflowY: 'auto',
                    paddingRight: '5px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        width: '24px',
                        color: '#9e9e9e',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>🩺</span>
                      <span style={{ 
                        fontSize: '14px',
                        wordBreak: 'break-word',
                        lineHeight: '1.4'
                      }}>{patient.radiology_exam}</span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        width: '24px',
                        color: '#9e9e9e',
                        flexShrink: 0
                      }}>🕒</span>
                      <span style={{ fontSize: '14px' }}>
                        {formatDateTime(patient.date_time)}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    flexShrink: 0
                  }}>
                    <button
                      onClick={() => handleShowRadiologyResults(patient)}
                      style={{
                        width: '100%',
                        maxWidth: '240px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1565c0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#1976d2';
                      }}
                    >
                      View/Edit Results
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Radiology Results Modal */}
      {isRadiologyResultsModalOpen && (
        <RadiologyResultsModal5
          patient={selectedPatient}
          clinicDetails={clinicDetails}
          onClose={handleCloseModal}
        />
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes urgent-blink {
          0%, 50% { background-color: #fff8f8; }
          25%, 75% { background-color: #ffebee; }
        }
        @keyframes urgent-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
      `}</style>
    </div>
  );
};

const StatCard = ({ title, value, icon }) => (
  <div style={{
    backgroundColor: '#fafafa',
    padding: '15px',
    borderRadius: '6px',
    border: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <div>
      <div style={{
        fontSize: '12px',
        color: '#757575',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '5px'
      }}>{title}</div>
      <div style={{
        fontSize: '24px',
        fontWeight: '600',
        color: '#212121'
      }}>{value}</div>
    </div>
    <div style={{
      fontSize: '24px',
      opacity: '0.7'
    }}>
      {icon}
    </div>
  </div>
);

export default RadiographerDashboard;